import { useEffect, useState } from "react";
import CollapsibleBody from "@/components/base/CollapsibleBody";
import { inputClass, primaryButtonClass, labelClass } from "@/components/base/fieldStyles";
import { formatNumber } from "@/utils/format";
import type { PvSummary } from "@/utils/pv";

interface PvPanelProps {
  summary: PvSummary;
  onSaveReferral: (monthKey: string, pv: number) => void;
}

// Puntos de Volumen (PV) acumulados: suman al ENTRAR mercancía al stock propio
// (cada compra). Los puntos de referidos se ingresan a mano, suman al mes en que
// se registran y se REINICIAN cada mes calendario.
const PvPanel = ({ summary, onSaveReferral }: PvPanelProps) => {
  const [referral, setReferral] = useState(String(summary.currentReferralPv));

  useEffect(() => {
    setReferral(String(summary.currentReferralPv));
  }, [summary.currentReferralPv, summary.currentMonthKey]);

  const referralValue = Math.max(0, Number(referral) || 0);
  const dirty = referralValue !== summary.currentReferralPv;

  return (
    <div className="glass-panel rounded-lg p-4 md:p-5">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-8 h-8 rounded-md bg-accent-500/25 text-accent-100 flex items-center justify-center">
          <i className="ri-star-smile-line" />
        </span>
        <h3 className="editable-title text-base font-bold">Puntos de Volumen (PV)</h3>
      </div>
      <p className="text-[11px] text-foreground-500 mb-4">
        Los PV suman <span className="text-foreground-300">al entrar mercancía a tu stock</span> (cada
        compra que registras). Las ventas no vuelven a sumar, para no duplicar. Los puntos de referidos
        los ingresas a mano, <span className="text-foreground-300">suman al mes en que los registras y se
        reinician cada mes</span>.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-lg bg-accent-500/12 border border-accent-500/25 px-4 py-3.5">
          <p className="text-[11px] text-foreground-500">PV global acumulado</p>
          <p className="text-2xl font-heading font-extrabold text-accent-100 mt-1">
            {formatNumber(summary.globalPv)} <span className="text-sm font-bold">PV</span>
          </p>
          <p className="text-[10px] text-foreground-500 mt-1">
            {formatNumber(summary.totalStockPv)} por compras + {formatNumber(summary.totalReferralPv)} por referidos
          </p>
        </div>
        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-4 py-3.5">
          <p className="text-[11px] text-foreground-500 flex items-center gap-1.5">
            <i className="ri-shopping-bag-3-line text-primary-200" /> PV por compras (stock)
          </p>
          <p className="text-xl font-bold text-foreground-950 mt-1">{formatNumber(summary.totalStockPv)} PV</p>
          <p className="text-[10px] text-foreground-500 mt-1">
            {formatNumber(summary.stockUnits)} unidades ingresadas
          </p>
        </div>
        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-4 py-3.5">
          <p className="text-[11px] text-foreground-500 flex items-center gap-1.5">
            <i className="ri-calendar-check-line text-secondary-200" /> PV que califica este mes
          </p>
          <p className="text-xl font-bold text-foreground-950 mt-1">
            {formatNumber(summary.currentQualifyingPv)} PV
          </p>
          <p className="text-[10px] text-foreground-500 mt-1 capitalize">
            {summary.currentMonthLabel} · {formatNumber(summary.currentStockPv)} compras +{" "}
            {formatNumber(summary.currentReferralPv)} referidos
          </p>
        </div>
      </div>

      <div className="mt-4 rounded-md bg-background-100/50 border border-background-300/40 p-3.5">
        <label className={labelClass}>
          Puntos de referidos de <span className="capitalize">{summary.currentMonthLabel}</span> (manual)
        </label>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <input
            type="number"
            min={0}
            value={referral}
            onChange={(e) => setReferral(e.target.value)}
            className={`${inputClass} sm:max-w-[200px]`}
          />
          <button
            onClick={() => onSaveReferral(summary.currentMonthKey, referralValue)}
            disabled={!dirty}
            className={`${primaryButtonClass} ${dirty ? "" : "opacity-50 cursor-not-allowed"}`}
          >
            <i className="ri-save-3-line" /> Guardar referidos
          </button>
        </div>
        <p className="text-[10px] text-foreground-500 mt-2">
          Estos puntos suman al PV que califica de este mes y se reinician automáticamente al empezar el
          mes siguiente.
        </p>
      </div>

      {summary.months.length === 0 ? (
        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-6 text-center mt-4">
          <i className="ri-inbox-line text-xl text-foreground-500" />
          <p className="text-xs text-foreground-500 mt-2">
            Aún no hay compras ni referidos registrados. Cada vez que ingreses mercancía a stock, sus PV
            suman aquí.
          </p>
        </div>
      ) : (
        <div className="mt-4">
          <CollapsibleBody count={summary.months.length} threshold={8} unit="meses">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[620px] text-left">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-foreground-500">
                    <th className="py-2 pr-3 font-semibold">Mes</th>
                    <th className="py-2 px-3 font-semibold text-center">Compras</th>
                    <th className="py-2 px-3 font-semibold text-right">Unidades</th>
                    <th className="py-2 px-3 font-semibold text-right">PV compras</th>
                    <th className="py-2 px-3 font-semibold text-right">Referidos</th>
                    <th className="py-2 pl-3 font-semibold text-right">Total mes</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.months.map((m) => (
                    <tr key={m.key} className="border-t border-background-300/40">
                      <td className="py-2.5 pr-3 text-xs font-semibold text-foreground-900 capitalize">
                        {m.label}
                        {m.key === summary.currentMonthKey && (
                          <span className="ml-2 text-[9px] font-bold uppercase text-accent-200 border border-accent-500/30 bg-accent-500/12 rounded px-1.5 py-0.5">
                            mes actual
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-center text-xs text-foreground-600">
                        {formatNumber(m.entries)}
                      </td>
                      <td className="py-2.5 px-3 text-right text-xs text-foreground-600">
                        {formatNumber(m.units)}
                      </td>
                      <td className="py-2.5 px-3 text-right text-xs font-semibold text-foreground-900 whitespace-nowrap">
                        {formatNumber(m.pv)} PV
                      </td>
                      <td className="py-2.5 px-3 text-right text-xs font-semibold text-secondary-200 whitespace-nowrap">
                        {formatNumber(m.referralPv)} PV
                      </td>
                      <td className="py-2.5 pl-3 text-right text-xs font-bold text-accent-200 whitespace-nowrap">
                        {formatNumber(m.totalPv)} PV
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CollapsibleBody>
        </div>
      )}
    </div>
  );
};

export default PvPanel;